import pdfMake from 'pdfmake/build/pdfmake';
import { viewPdfFile, downloadPdfFile } from 'src/helpers/pdf';
import {
  formatCurrencyAmount,
  formatIDNumberText,
  formatPhoneNumberText,
  formatMonthlyAmount,
} from 'src/helpers/formatting';
import BottomLeft from 'src/assets/images/vodapaylogov3.png';
import { AcquiringChannelFlags } from 'src/context/salesforce-context';
import { CustomerTypes } from 'src/context/salesforce-context';
import { isVirtualorInstoreDevice } from 'src/helpers/devices';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import { PRODUCT_IMAGE_MAP } from 'src/features/order/tech-debt/product-details-extra';

const formatOnceOffOrMonthly = (value, onceOff) =>
  onceOff ? `R ${formatCurrencyAmount(value)}` : formatMonthlyAmount(value);

const VAT = 0.15;

const orderBuilder = async (
  merchant,
  lead,
  deliveries,
  getDeviceOrderQuantity,
  terminals,
  deliveryAddresses,
  totalPrice,
  onceOffTotal,
  customerType,
) => ({
  yourSolution: deliveries
    .filter(
      (delivery) =>
        !delivery.product_category || (!!delivery.product_category && delivery.is_product_category),
    )
    .map((delivery) => {
      const freeValue = formatMonthlyAmount(0);
      const hasOnceOffCost =
        delivery.acquiring_channel === AcquiringChannelFlags.buyOnlineFicaOnline;
      const isFree =
        delivery.price === 0 || delivery.price === undefined || delivery.price === null;
      const quantity = getDeviceOrderQuantity(delivery.device_id);
      const individualPrice = !isFree
        ? formatMonthlyAmount(delivery.price)
        : formatMonthlyAmount(0);
      const vat = !isFree
        ? formatOnceOffOrMonthly(delivery.price * quantity * VAT, hasOnceOffCost)
        : formatOnceOffOrMonthly(0, hasOnceOffCost);
      const subTotal = !isFree
        ? formatOnceOffOrMonthly(delivery.price * quantity, hasOnceOffCost)
        : freeValue;
      const total =
        totalPrice !== null && totalPrice !== undefined
          ? formatMonthlyAmount(totalPrice)
          : freeValue;
      const isInStoreOrVirtual =
        isVirtualorInstoreDevice(delivery.acquiring_channel) ||
        delivery.acquiring_channel === AcquiringChannelFlags.vodacomBusiness;
      const onceOffPrice = `R ${formatCurrencyAmount(delivery.price)} once off`;
      const showOnceOffTotal =
        onceOffTotal !== null && onceOffTotal !== undefined && onceOffTotal > 0;
      const totalOnceOff =
        onceOffTotal !== null && onceOffTotal !== undefined
          ? `R ${formatCurrencyAmount(onceOffTotal)}`
          : ``;

      const terminalInfo = terminals[delivery.device_id];
      return {
        productName: delivery.device_name,
        productImage: PRODUCT_IMAGE_MAP[terminalInfo?.name],
        isFree: isFree,
        vat: vat,
        individualPrice: hasOnceOffCost ? onceOffPrice : individualPrice,
        quantity: quantity.toString(),
        subTotal: subTotal,
        total: total,
        isInStoreOrVirtual: isInStoreOrVirtual,
        boxContents: terminalInfo?.set_contains
          ? terminalInfo.set_contains.split(/\r?\n/).map((value) => `- ${value}`)
          : [],
        deviceInformation: terminalInfo?.device_information
          ? terminalInfo.device_information.split(/\r?\n/).map((value) => `- ${value}`)
          : [],
        hasOnceOffCost: hasOnceOffCost,
        onceOffPrice: onceOffPrice,
        showOnceOffTotal: showOnceOffTotal,
        totalOnceOff: totalOnceOff,
        customerType: customerType,
        consumerType: lead.consumerType,
        isBizWallet: delivery.acquiring_channel === AcquiringChannelFlags.vodapay,
        isVodapayBusiness: delivery.acquiring_channel === AcquiringChannelFlags.vodacomBusiness,
      };
    }),
  del: deliveryAddresses
    ?.filter((delivery) => delivery.acquiring_channel !== AcquiringChannelFlags.vodapay)
    .map((delivery) => {
      const address = ['street', 'suburb', 'city', 'region', 'state', 'zip_code']
        .map((addressItem) => delivery?.[addressItem])
        .filter((item) => item !== '' && item !== undefined)
        .join('\n');

      let newDeviceList = {};
      Object.entries(delivery.devices)
        .filter(
          (deviceList) => !isVirtualorInstoreDevice(terminals[deviceList[0]]?.acquiring_channel),
        )
        .forEach((device) => (newDeviceList[device[0]] = device[1]));

      const numDevices = Object.entries(newDeviceList).reduce(
        (quantityA, quantityB) => quantityA + quantityB[1],
        0,
      );

      const devices = Object.keys(newDeviceList)
        .map((deviceId) => terminals[deviceId]?.name)
        .join(', ');
      return {
        address: address,
        numberOfDevices: numDevices,
        contactPerson: delivery.manager_name,
        workNumber: delivery.phone_work || delivery.phone_cell,
        devices: devices,
        tradingName: delivery.trading_as,
        idNumber: delivery.id_number,
        cellNumber: delivery.phone_cell || delivery.phone_work,
      };
    }),
  yourBusiness: {
    name: merchant.account.business_name,
    monthlyTurnover: `R ${formatCurrencyAmount(lead.monthlyCardTurnover)}`,
    address: ['street', 'suburb', 'city', 'region', 'state', 'zip_code']
      .map((addressItem) => merchant?.account?.[addressItem])
      .filter((item) => item !== '' && item !== undefined)
      .join('\n'),
    type: lead.typeOfBusiness,
    industry: merchant.account.industry,
    nature: merchant.account.nature_of_business,
  },
  yourDetails: {
    selfie: merchant.contact.contact_photo,
    firstName: merchant.contact.first_names,
    surname: merchant.contact.surname,
    contactNumber: merchant.contact.phone,
    email: merchant.contact.email,
    id: merchant.contact.id_number,
    idType: merchant.contact.id_type,
  },
  lead: lead,
});

const getSolution = (solution, index) => [
  {
    unbreakable: true,
    style: ['tableExample', 'small'],
    table: {
      headerRows: 1,
      widths: [80, 180, '*', '*'],
      heights: [10, 0, 30],
      body: [
        [
          {
            text:
              index === 0
                ? (solution.consumerType === CONSUMER_TYPE.assistant &&
                    solution.customerType !== CustomerTypes.pos) ||
                  solution.customerType === CustomerTypes.agent
                  ? 'Customer solutions'
                  : 'Your Solutions'
                : '',
            style: 'headerNames',
            colSpan: 2,
          },
          '',
          '',
          '',
        ],
        [
          {
            image: `${!!solution.productImage ? solution.productImage : BottomLeft}`,
            width: 60,
            rowSpan: 4,
          },
          { text: solution.productName, style: 'deviceNames' },
          !solution.isInStoreOrVirtual
            ? { text: 'Unit price', style: [{ alignment: 'right' }] }
            : '',
          !solution.isInStoreOrVirtual
            ? {
                text:
                  !solution.isFree || solution.hasOnceOffCost
                    ? `${solution.individualPrice}`
                    : 'No monthly rental costs',
                style: ['tableHeader', 'medium', { alignment: 'right' }],
              }
            : '',
        ],
        [
          '',
          {
            canvas: [
              {
                type: 'line',
                x1: 3,
                y1: 10,
                x2: 40,
                y2: 10,
                lineWidth: 3.5,
                lineCap: 'round',
                lineColor: '#e60000',
              },
            ],
          },
          solution.isBizWallet ? '' : { text: 'Quantity', style: [{ alignment: 'right' }] },
          solution.isBizWallet
            ? ''
            : { text: solution.quantity, style: ['tableHeader', 'medium', { alignment: 'right' }] },
        ],
        [
          '',
          solution.boxContents?.length > 0
            ? { text: 'What you get in the box', style: ['medium', 'tableHeader'] }
            : '',
          !solution.isInStoreOrVirtual
            ? {
                text: !solution.isFree || !solution.hasOnceOffCost ? 'Sub total (excl.VAT)' : '',
                style: [{ alignment: 'right' }],
              }
            : '',
          !solution.isInStoreOrVirtual
            ? {
                text: !solution.isFree || !solution.hasOnceOffCost ? solution.subTotal : '',
                style: ['tableHeader', 'medium', { alignment: 'right' }],
              }
            : {
                text: !solution.isBizWallet && !solution.isVodapayBusiness ? 'Pay in store' : ' ',
                style: ['tableHeader', 'medium', { alignment: 'right' }],
              },
        ],
        [
          '',
          solution.boxContents?.length > 0 ? { type: 'none', ul: solution.boxContents } : '',
          !solution.isInStoreOrVirtual
            ? {
                text: !solution.isFree || !solution.hasOnceOffCost ? 'VAT' : '',
                style: [{ alignment: 'right' }],
              }
            : '',
          !solution.isInStoreOrVirtual
            ? {
                text: !solution.isFree || !solution.hasOnceOffCost ? solution.vat : '',
                style: ['tableHeader', 'medium', { alignment: 'right' }],
              }
            : '',
        ],
      ],
    },
    layout: 'noBorders',
  },
  { text: '\n\n' },
];

const getDeliveryAddresses = (address, index) => [
  {
    stack: [
      index === 0 ? { text: 'Delivery address', style: 'subheader' } : {},
      index === 0
        ? {
            text: 'Device shipped within 5 business days on application approval.\n\n',
            style: 'plainText',
          }
        : {},
      {
        style: ['tableExample', 'small'],
        table: {
          headerRows: 1,
          widths: ['*', '*', '*'],
          heights: [10, 22, 10, 22, 10],
          body: [
            [
              { text: 'Business address', style: ['tableHeaderLarge'] },
              { text: 'Trading name', style: 'tableHeaderLarge' },
              { text: 'Number of devices', style: 'tableHeaderLarge' },
            ],
            [{ rowSpan: 3, text: address.address }, address.tradingName, address.numberOfDevices],
            [
              '',
              { text: 'Contact person', style: 'tableHeaderLarge' },
              { text: 'ID number', style: 'tableHeaderLarge' },
            ],
            ['', address.contactPerson, formatIDNumberText(address.idNumber)],
            [
              { text: 'Cell number', style: 'tableHeaderLarge' },
              { text: 'Work number', style: 'tableHeaderLarge' },
              { text: 'Devices', style: 'tableHeaderLarge' },
            ],
            [
              formatPhoneNumberText(address.cellNumber),
              formatPhoneNumberText(address.workNumber),
              address.devices,
            ],
          ],
        },
        layout: 'noBorders',
      },
      { text: '\n\n' },
    ],
    unbreakable: true,
  },
];

const docDefinitionBuilder = (order) => ({
  footer: {
    columns: [
      { image: `${BottomLeft}`, width: 96, margin: [100, 5, 0, 0] },
      // { image: `${BottomRight}`, fit: [91, 35], margin: [300, 0, 0, 0] },
    ],
  },
  content: [
    {
      columns: [
        { image: `${BottomLeft}`, width: 170 },
        {
          text:
            (order.yourSolution[0].consumerType === CONSUMER_TYPE.assistant &&
              order.yourSolution[0].customerType !== CustomerTypes.pos) ||
            order.yourSolution[0].customerType === CustomerTypes.agent
              ? 'Confirm the application'
              : 'Confirm your application',
          style: 'header',
          margin: [80, 10, 0, 0],
        },
      ],
    },
    { text: '\n\n' },
    {
      table: {
        widths: ['*'],
        body: [[{ stack: [...order.yourSolution.map(getSolution)] }]],
      },
      layout: {
        hLineColor: function() {
          return '#C9CCD4';
        },
        vLineColor: function() {
          return '#C9CCD4';
        },
        paddingLeft: function() {
          return 20;
        },
        paddingRight: function() {
          return 20;
        },
        paddingTop: function() {
          return 20;
        },
        paddingBottom: function() {
          return 20;
        },
        hLineWidth: function() {
          return 1;
        },
        vLineWidth: function() {
          return 1;
        },
      },
    },
    { text: '\n\n' },
    (order.yourSolution.length === 1 && order.yourSolution[0].isBizWallet) ||
    order.yourSolution[0].isVodapayBusiness
      ? { text: '' }
      : {
          layout: 'noBorders',
          table: {
            dontBreakRows: true,
            widths: ['*', '*'],
            style: 'totalTable',
            body: [
              [
                {
                  text: 'Total (incl.VAT)',
                  style: ['tableHeader', { color: '#ffffff', fillColor: '#e60000' }],
                  margin: [25, 25, 25, 25],
                },
                {
                  text: order.yourSolution[0].total,
                  style: [
                    'tableHeader',
                    { alignment: 'right', color: '#ffffff', fillColor: '#e60000' },
                  ],
                  margin: [25, 25, 25, 25],
                },
              ],
              order.yourSolution[0].showOnceOffTotal
                ? [
                    {
                      text: 'Total due now (incl.VAT)',
                      style: ['tableHeader', { color: '#ffffff', fillColor: '#e60000' }],
                      margin: [25, 25, 25, 25],
                    },
                    {
                      text: order.yourSolution[0].totalOnceOff,
                      style: [
                        'tableHeader',
                        { alignment: 'right', color: '#ffffff', fillColor: '#e60000' },
                      ],
                      margin: [25, 25, 25, 25],
                    },
                  ]
                : ['', ''],
            ],
          },
        },
    { text: '\n\n' },
    !!order.del?.length > 0
      ? {
          table: {
            widths: ['*'],
            body: [...(order.del?.map(getDeliveryAddresses) || [])],
          },
          layout: {
            hLineColor: function() {
              return '#C9CCD4';
            },
            vLineColor: function() {
              return '#C9CCD4';
            },
            paddingLeft: function() {
              return 20;
            },
            paddingRight: function() {
              return 20;
            },
            paddingTop: function() {
              return 20;
            },
            paddingBottom: function() {
              return 20;
            },
            hLineWidth: function() {
              return 1;
            },
            vLineWidth: function() {
              return 1;
            },
          },
        }
      : { text: '' },
    { text: '\n\n' },
    {
      table: {
        widths: ['*'],
        body: [
          [
            {
              stack: [
                {
                  text:
                    (order.yourSolution[0].consumerType === CONSUMER_TYPE.assistant &&
                      order.yourSolution[0].customerType !== CustomerTypes.pos) ||
                    order.yourSolution[0].customerType === CustomerTypes.agent
                      ? 'Business information\n\n'
                      : 'Your business information\n\n',
                  style: 'subheader',
                },
                {
                  style: ['tableExample', 'small'],
                  table: {
                    headerRows: 1,
                    widths: ['*', '*', '*'],
                    heights: [10, 22],
                    body: [
                      [
                        { text: 'Business name', style: ['tableHeaderLarge'] },
                        { text: 'Monthly income', style: 'tableHeaderLarge' },
                        { text: 'Business address', style: 'tableHeaderLarge' },
                      ],
                      [
                        order.yourBusiness.name,
                        order.yourBusiness.monthlyTurnover,
                        order.yourBusiness.address,
                      ],
                      [
                        { text: 'Business type', style: ['tableHeaderLarge'] },
                        { text: 'Industry', style: 'tableHeaderLarge' },
                        { text: 'Business nature', style: 'tableHeaderLarge' },
                      ],
                      [
                        order.yourBusiness.type,
                        order.yourBusiness.industry,
                        order.yourBusiness.nature,
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
              ],
              unbreakable: true,
            },
          ],
        ],
      },
      layout: {
        hLineColor: function() {
          return '#C9CCD4';
        },
        vLineColor: function() {
          return '#C9CCD4';
        },
        paddingLeft: function() {
          return 20;
        },
        paddingRight: function() {
          return 20;
        },
        paddingTop: function() {
          return 20;
        },
        paddingBottom: function() {
          return 20;
        },
        hLineWidth: function() {
          return 1;
        },
        vLineWidth: function() {
          return 1;
        },
      },
    },
    { text: '\n\n' },
    {
      table: {
        widths: ['*'],
        body: [
          [
            {
              stack: [
                {
                  text:
                    (order.yourSolution[0].consumerType === CONSUMER_TYPE.assistant &&
                      order.yourSolution[0].customerType !== CustomerTypes.pos) ||
                    order.yourSolution[0].customerType === CustomerTypes.agent
                      ? 'Personal information\n\n'
                      : 'Your personal information\n\n',
                  style: 'subheader',
                },
                {
                  style: ['tableExample', 'small'],
                  table: {
                    headerRows: 4,
                    widths: [100, 120, '*', '*'],
                    heights: [10, 22],
                    body: [
                      [
                        {
                          image: `data:image/jpeg;base64,${order.yourDetails.selfie}`,
                          width: 100,
                          rowSpan: 4,
                        },
                        { text: 'First name', style: 'tableHeaderLarge' },
                        { text: 'Email', style: 'tableHeaderLarge' },
                        { text: 'Contact number', style: 'tableHeaderLarge' },
                      ],
                      [
                        '',
                        order.yourDetails.firstName,
                        order.yourDetails.email,
                        formatPhoneNumberText(order.yourDetails.contactNumber),
                      ],
                      [
                        { text: '', style: 'tableHeaderLarge' },
                        { text: 'Surname', style: ['tableHeaderLarge'] },
                        {
                          text: order.yourDetails.idType === 'id' ? 'ID' : 'Passport',
                          style: 'tableHeaderLarge',
                        },
                        { text: '', style: 'tableHeaderLarge' },
                      ],
                      [
                        '',
                        order.yourDetails.surname,
                        order.yourDetails.idType === 'id'
                          ? formatIDNumberText(order.yourDetails.id)
                          : order.yourDetails.id,
                        '',
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
              ],
              unbreakable: true,
            },
          ],
        ],
      },
      layout: {
        hLineColor: function() {
          return '#C9CCD4';
        },
        vLineColor: function() {
          return '#C9CCD4';
        },
        paddingLeft: function() {
          return 20;
        },
        paddingRight: function() {
          return 20;
        },
        paddingTop: function() {
          return 20;
        },
        paddingBottom: function() {
          return 20;
        },
        hLineWidth: function() {
          return 1;
        },
        vLineWidth: function() {
          return 1;
        },
      },
    },
    order.yourSolution[0].customerType !== CustomerTypes.normal &&
    order.yourSolution[0].customerType !== CustomerTypes.pos
      ? {
          stack: [
            {
              text: '\n\n',
            },
            {
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      stack: [
                        { text: 'Agent information\n\n', style: 'subheader' },
                        {
                          style: ['tableExample', 'small'],
                          table: {
                            headerRows: 1,
                            widths: [100, 120, '*', '*'],
                            heights: [10, 22],
                            body: [
                              [
                                {
                                  text:
                                    order.yourSolution[0].customerType === CustomerTypes.dealer
                                      ? 'Dealer ID'
                                      : 'Agent ID',
                                  style: 'tableHeaderLarge',
                                },
                                '',
                                '',
                                '',
                              ],
                              [
                                order.yourSolution[0].customerType === CustomerTypes.dealer
                                  ? order.lead.dealerId
                                  : order.lead.agentId,
                                '',
                                '',
                                '',
                              ],
                            ],
                          },
                          layout: 'noBorders',
                        },
                      ],
                      unbreakable: true,
                    },
                  ],
                ],
              },
              layout: {
                hLineColor: function() {
                  return '#C9CCD4';
                },
                vLineColor: function() {
                  return '#C9CCD4';
                },
                paddingLeft: function() {
                  return 20;
                },
                paddingRight: function() {
                  return 20;
                },
                paddingTop: function() {
                  return 20;
                },
                paddingBottom: function() {
                  return 20;
                },
                hLineWidth: function() {
                  return 1;
                },
                vLineWidth: function() {
                  return 1;
                },
              },
            },
          ],
        }
      : '',
  ],
  styles: {
    header: {
      fontSize: 24,
      bold: true,
      color: '#e60000',
    },
    headerNonBold: {
      fontSize: 16,
      lineHeight: 2,
    },
    subheader: {
      fontSize: 16,
      bold: true,
      margin: [4, 0, 0, 0],
    },
    plainText: {
      margin: [4, 0, 0, 0],
    },
    small: {
      fontSize: 10,
    },
    medium: {
      fontSize: 11,
    },
    tableExample: {
      margin: [5, 5, 5, 5],
    },
    tableHeader: {
      bold: true,
      fontSize: 13,
      color: 'black',
    },
    tableHeaderLarge: {
      fontSize: 8,
      color: '#393949',
    },
    deviceNames: {
      bold: true,
      fontSize: 16,
      color: 'black',
    },
    headerNames: {
      bold: true,
      fontSize: 16,
      color: 'black',
      margin: [0, 0, 0, 15], // margin: [left, top, right, bottom]
    },
    totalTable: {
      color: '#f2f4f7',
      fillColor: '#e60000',
    },
  },
});

export const viewSavedContract = (base64Document) => {
  viewPdfFile(base64Document, 'VodaPay device order.pdf');
};

export const downloadSavedContract = (base64Document) => {
  downloadPdfFile(base64Document, 'VodaPay device order.pdf');
};

export const getContract = async (
  merchant,
  lead,
  deliveries,
  getDeviceOrderQuantity,
  terminals,
  deviceQuantitiesToDisplay,
  totalPrice,
  onceOffTotal,
  customerType,
  storePdf,
) => {
  const documentDefinition = docDefinitionBuilder(
    await orderBuilder(
      merchant,
      lead,
      deliveries,
      getDeviceOrderQuantity,
      terminals,
      deviceQuantitiesToDisplay,
      totalPrice,
      onceOffTotal,
      customerType,
    ),
  );
  const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
  pdfDocGenerator.getBase64((data) => {
    storePdf('data:application/pdf;base64,' + data);
  });
};
export const downloadContract = async (
  merchant,
  lead,
  deliveries,
  getDeviceOrderQuantity,
  terminals,
  deviceQuantitiesToDisplay,
  totalPrice,
  onceOffTotal,
  customerType,
) => {
  const documentDefinition = docDefinitionBuilder(
    await orderBuilder(
      merchant,
      lead,
      deliveries,
      getDeviceOrderQuantity,
      terminals,
      deviceQuantitiesToDisplay,
      totalPrice,
      onceOffTotal,
      customerType,
    ),
  );
  const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
  pdfDocGenerator.getBase64((data) => {
    downloadPdfFile('data:application/pdf;base64,' + data, 'VodaPay device order.pdf');
  });
};

export const viewContract = async (
  merchant,
  lead,
  deliveries,
  getDeviceOrderQuantity,
  terminals,
  deviceQuantitiesToDisplay,
  totalPrice,
  onceOffTotal,
  customerType,
) => {
  const documentDefinition = docDefinitionBuilder(
    await orderBuilder(
      merchant,
      lead,
      deliveries,
      getDeviceOrderQuantity,
      terminals,
      deviceQuantitiesToDisplay,
      totalPrice,
      onceOffTotal,
      customerType,
    ),
  );
  const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
  pdfDocGenerator.getBase64((data) => {
    viewPdfFile('data:application/pdf;base64,' + data, 'VodaPay device order.pdf');
  });
};
